import type { AuthenticatedUser, MembershipSummary } from '@upweo/types';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { restoreSession, setAccessToken, setActiveShop } from './api-client.js';
import * as api from './api.js';
import { type AuthState, AuthContext } from './auth-context.js';

export const AuthProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [activeStore, setActiveStore] = useState<MembershipSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const adopt = useCallback((next: AuthenticatedUser) => {
    setUser(next);
    const first = next.memberships[0] ?? null;
    setActiveStore(first);
    setActiveShop(first?.tenantSlug ?? null);
  }, []);

  /**
   * On mount, try to rebuild the session from the httpOnly refresh cookie.
   * This is what makes a page reload keep you signed in without ever putting
   * a token in localStorage.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        if (await restoreSession()) {
          const { user: restored } = await api.me();
          if (!cancelled) {
            adopt(restored);
          }
        }
      } catch {
        // No cookie, or it expired — remain signed out.
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adopt]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const result = await api.login({ email, password });
      setAccessToken(result.accessToken);
      adopt(result.user);
    },
    [adopt],
  );

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // Clear locally even if the request failed, so the UI cannot be stuck
      // in a signed-in state with a dead session.
      setAccessToken(null);
      setActiveShop(null);
      setUser(null);
      setActiveStore(null);
    }
  }, []);

  const selectStore = useCallback(
    async (tenantId: string) => {
      const next = user?.memberships.find((m) => m.tenantId === tenantId);
      if (!next) {
        return;
      }
      // Persist server-side too, so a reload returns to this store.
      await api.switchStore(tenantId);
      setActiveShop(next.tenantSlug);
      setActiveStore(next);
    },
    [user],
  );

  const refresh = useCallback(async () => {
    const { user: current } = await api.me();
    adopt(current);
  }, [adopt]);

  const value = useMemo<AuthState>(
    () => ({ user, activeStore, isLoading, signIn, signOut, selectStore, refresh }),
    [user, activeStore, isLoading, signIn, signOut, selectStore, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
